import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GenerateMockupInput, GenerateMockupResponse, Mockup, MockupHistoryItem } from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useMockups(workspaceId: string | null, quoteId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'quotes', quoteId, 'mockups'],
    queryFn: () => api.get<Mockup[]>(workspacePath(workspaceId!, `/quotes/${quoteId}/mockups`)),
    enabled: !!workspaceId && !!quoteId,
    refetchInterval: (query) =>
      query.state.data?.some((m) => m.status === 'pending') ? 3000 : false,
  });
}

export function useVehicleMockupHistory(workspaceId: string | null, vehicleId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'vehicles', vehicleId, 'mockups'],
    queryFn: () => api.get<MockupHistoryItem[]>(workspacePath(workspaceId!, `/vehicles/${vehicleId}/mockups`)),
    enabled: !!workspaceId && !!vehicleId,
  });
}

export function useAttachExistingMockup(workspaceId: string | null, quoteId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mockupId: string) =>
      api.post<{ mockup: Mockup }>(workspacePath(workspaceId!, `/quotes/${quoteId}/mockups/attach-existing`), { mockupId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'quotes', quoteId, 'mockups'] });
    },
  });
}

export function useGenerateMockup(workspaceId: string | null, quoteId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateMockupInput) =>
      api.post<GenerateMockupResponse>(workspacePath(workspaceId!, `/quotes/${quoteId}/mockups`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'quotes', quoteId, 'mockups'] });
    },
  });
}

export function useSetMockupAttached(workspaceId: string | null, quoteId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mockupId, attached }: { mockupId: string; attached: boolean }) =>
      api.patch<void>(workspacePath(workspaceId!, `/quotes/${quoteId}/mockups/${mockupId}`), { attached }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'quotes', quoteId, 'mockups'] });
    },
  });
}
